import {
    ColumnType,
    FileUploadRes,
    yascAutocomplete,
    yascClassDisplayName,
    yascClassFormSectionTitle,
    yascCollection,
    yascDisplayName,
    yascEntityName,
    yascField,
    yascFormGrid,
    yascFormIgnore,
    yascFormIndex,
    yascFormKind,
    yascFormRows,
    yascMaxLength,
    yascMinLength,
    yascNested,
    yascOptionsKey,
    yascRequired,
    yascSelect,
    yascTableColumn,
    yascTableDisplayFn,
    yascTableIgnore,
    yascTableIndex,
    yascTableLevelOfDetail
} from '@yasc/yasc-material-components';
import {TaskRelationTypeEnum} from '../../../../enums/task-relation-type.enum';
import {Type} from 'class-transformer';
import {ProjectMinRes} from '../tracker/project-tracker';

@yascClassDisplayName('Effort')
@yascClassFormSectionTitle('Effort')
export class TaskEffort {
    @yascTableIgnore()
    @yascFormIgnore()
    id!: string;

    @yascDisplayName('general.common.name')
    @yascRequired()
    name!: string;

    @yascDisplayName('general.common.description')
    @yascRequired()
    description!: string;


    @yascDisplayName('crm.task.shared.effortType')
    @yascRequired()
    @yascOptionsKey('effortType')
    @yascRequired()
    @yascSelect()
    @yascTableColumn(ColumnType.Select)
    effortTypeId!:string;

    @yascTableIgnore()
    @yascFormIgnore()
    taskId!: string;

    @yascDisplayName('general.dateTime.estimatedDuration')
    @yascFormKind('number')
    estimatedDuration!: number;

    @yascDisplayName('general.dateTime.actualDuration')
    @yascFormIgnore()
    actualDuration!: number;

    @yascTableIgnore()
    @yascFormIgnore()
    index!: number;


    constructor(value:Partial<TaskEffort> = {}) {
        Object.assign(this, value)
    }
}

@yascClassDisplayName('TaskReference')
@yascClassFormSectionTitle('TaskReference')
export class TaskReference {
    @yascFormIgnore()
    @yascTableIgnore()
    id?: string;

    @yascField()
    @yascRequired()
    @yascFormGrid({row: 0, width: 1})
    @yascSelect()
    @yascDisplayName('crm.task.relation.connection')
    @yascOptionsKey('relationType')
    @yascTableColumn(ColumnType.Select)
    type: TaskRelationTypeEnum = TaskRelationTypeEnum.RelatedTo;

    @yascField()
    @yascRequired()
    @yascFormGrid({row: 0, width: 1})
    @yascFormKind('autocomplete')
    @yascDisplayName('crm.task.relation.toTask')
    @yascOptionsKey('referenceTask')
    @yascTableColumn(ColumnType.Select)
    ReferencedTask: string = '';

    constructor(value: Partial<TaskReference> = {}) {
        Object.assign(this, value)
    }
}

@yascEntityName('')
@yascClassDisplayName('')
@yascClassFormSectionTitle('crm_task_details_sectionTitle')
export class BaseTask {
    @yascTableIgnore()
    @yascFormIgnore()
    id:string= '';

    @yascDisplayName('general_name')
    @yascTableIndex(0)
    @yascTableLevelOfDetail(0)
    @yascFormGrid({row: 0,column:1})
    @yascRequired()
    @yascMinLength(1)
    @yascMaxLength(255)
    @yascFormIndex(0)
    name: string = '';

    @yascDisplayName('crm_task_taskId_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    taskID: string = '';

    @yascDisplayName('general_category')
    @yascFormIgnore()
    @yascTableIgnore()
    category:string = '';

    @yascDisplayName('crm_task_mockup_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    mockups: FileUploadRes[] = [];

    @yascDisplayName('crm_contract_taskRisks_label')
    @yascTableColumn(ColumnType.Tag)
    @yascTableDisplayFn((val: string[]) => val.join(', '))
    @yascTableLevelOfDetail(1)
    @yascFormIgnore()
    risks: string[] = [];

    @yascDisplayName('crm_contract_taskClarifications_label')
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Tag)
    @yascTableDisplayFn((val: string[]) => val.join(', '))
    @yascTableLevelOfDetail(1)
    @yascFormIgnore()
    clarifications: string[] = [];

    @yascDisplayName('crm_task_currentPlanned_label')
    @yascTableColumn(ColumnType.Number)
    @yascTableLevelOfDetail(2)
    @yascFormIgnore()
    currentPlanned: number = 0;

    @yascFormIgnore()
    @yascTableIgnore()
    estimated!: number;

    @yascDisplayName('general_description')
    @yascTableLevelOfDetail(2)
    @yascFormKind('textarea')
    @yascFormRows(5)
    @yascFormGrid({fullRow: true, row:5})
    @yascFormIndex(10)
    description: string | undefined = '';

    @yascDisplayName('general_project')
    @yascAutocomplete()
    @yascOptionsKey('project')
    @yascFormIndex(5)
    @yascTableIgnore(true)
    @yascRequired()
    @yascFormGrid({row: 0, column:2})
    projectId: number | undefined = 0

    @yascDisplayName('general_project')
    @yascTableColumn(ColumnType.Select)
    @yascFormIgnore()
    @yascTableIndex(5)
    @yascTableDisplayFn((val:ProjectMinRes, row) => val.name)
    project:ProjectMinRes |undefined = undefined

    @yascDisplayName('crm_task_recentAppointments_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    recentAppointmentsCnt: number = 0;

    @yascDisplayName('crm_task_currentStart_label')
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(1)
    @yascFormIndex(2)
    @yascFormKind('date')
    @Type(() => Date)
    start!: string;

    @yascDisplayName('crm_task_currentEnd_label')
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(1)
    @yascFormIndex(3)
    @yascFormKind('date')
    @Type(() => Date)
    end!: string;

    @yascDisplayName('crm_contract_connectedContacts_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    contactIds: number[] = [];

    @yascDisplayName('crm_contract_referencedTasks_label')
    @yascTableIgnore()
    @yascNested(TaskReference)
    @yascCollection({
        itemIdKey:'id',
        editOnRowClick: true,
        createItem: () => new TaskReference({
            ReferencedTask:'',
            type:TaskRelationTypeEnum.RelatedTo
        })
    })
    references: TaskReference[] = []

    @yascDisplayName('crm.task.shared.effort')
    @yascNested(TaskEffort)
    @yascCollection({
        itemIdKey:'id',
        createItem: () => new TaskEffort()
    })
    efforts: TaskEffort[] = [];

    @yascFormIgnore()
    @yascDisplayName('crm.task.list.projectRequiresEfforts')
    @yascTableDisplayFn((value: boolean | null): string => {
        let text: string = ''
        if (value !== null) text = value ? '!' : '✓'
        return text
    })
    get needsEfforts(): boolean | null {
        if (this.project !== undefined && this.project.requireEfforts)
            return this.efforts.length === 0
        return null
    }

    constructor(value?: Partial<BaseTask>) {
        Object.assign(this, value)
    }
}
